import { chain } from '@leprecoins/effector';
import { createForm } from '@leprecoins/effector-forms';
import { combine, sample } from 'effector';
import { createBlockCashdeskModel } from 'features/block-cashdesk';
import { createDeleteCashdeskModel } from 'features/delete-cashdesk';
import { createDepositCashdeskModel } from 'features/deposit-cashdesk';
import { createUnblockCashdeskModel } from 'features/unblock-cashdesk';
import { createWithdrawCashdeskModel } from 'features/withdraw-cashdesk';
import {
	CASHDESK_NAME_MIN_LENGTH,
	CASHDESK_NOTE_MAX_LENGTH,
	combineCashiersMultiselectData,
} from 'entities/cashdesk';
import { createEditCashdeskMutation, createGetCashdeskQuery, getCashiersQuery } from 'shared/api';
import { maxLengthRule, minLengthRule, notEmptyRule, notify } from 'shared/lib';
import { authorizedCashdeskRoute, router } from 'shared/routing';

const getCashdeskQuery = createGetCashdeskQuery();

const editCashdeskMutation = createEditCashdeskMutation();

const withdrawCashdeskModel = createWithdrawCashdeskModel();

const depositCashdeskModel = createDepositCashdeskModel();

const blockCashdeskModel = createBlockCashdeskModel();

const unblockCashdeskModel = createUnblockCashdeskModel();

const deleteCashdeskModel = createDeleteCashdeskModel();

const form = createForm({
	fields: {
		name: {
			init: '',
			rules: [
				notEmptyRule,
				minLengthRule(CASHDESK_NAME_MIN_LENGTH),
				maxLengthRule(CASHDESK_NOTE_MAX_LENGTH),
			],
		},
		account: {
			init: '',
		},
		currency: {
			init: '',
		},
		balance: {
			init: '',
		},
		cashierIds: {
			init: [] as number[],
		},
		note: {
			init: '',
			rules: [maxLengthRule(CASHDESK_NOTE_MAX_LENGTH)],
		},
	},
	validateOn: ['submit'],
	resetErrorsOnChange: true,
});

export const $cashdesk = getCashdeskQuery.$data;

const $cashiers = combineCashiersMultiselectData(
	getCashiersQuery.$data.map((cashiers) => cashiers ?? []),
	$cashdesk.map((cashdesk) => cashdesk?.cashiers ?? []),
);

const $pending = combine(
	{
		cashdesk: $cashdesk,
		pending: getCashdeskQuery.$pending,
	},
	({ cashdesk, pending }) => !cashdesk && pending,
);

sample({
	clock: authorizedCashdeskRoute.opened,
	fn: ({ params }) => params.id,
	target: getCashdeskQuery.start,
});

sample({
	clock: getCashdeskQuery.finished.success,
	fn: ({ result }) => ({
		name: result.name,
		account: result.account.name,
		currency: result.currency,
		balance: result.balance.toFixed(2),
		cashierIds: result.cashiers.map((cashier) => cashier.id),
		note: result.note ?? '',
	}),
	target: [form.setInitialValues, form.setValues],
});

sample({
	clock: getCashdeskQuery.finished.success,
	fn: ({ result }) => result.account.id,
	target: getCashiersQuery.start,
});

sample({
	clock: form.submitted,
	source: { values: form.$values, cashdesk: $cashdesk },
	filter: ({ cashdesk }) => cashdesk !== null,
	fn: ({ values, cashdesk }) => ({
		id: cashdesk!.id,
		...values,
	}),
	target: editCashdeskMutation.start,
});

notify(editCashdeskMutation.finished.success, 'cashdesk-page.edit-success');

chain(editCashdeskMutation.finished.success, getCashiersQuery.reset);

sample({
	clock: [
		depositCashdeskModel.depositCashdeskMutation.finished.success,
		withdrawCashdeskModel.withdrawCashdeskMutation.finished.success,
		editCashdeskMutation.finished.success,
		blockCashdeskModel.mutation.finished.success,
		unblockCashdeskModel.mutation.finished.success,
	],
	source: $cashdesk,
	filter: Boolean,
	fn: ({ id }) => id,
	target: getCashdeskQuery.start,
});

chain(deleteCashdeskModel.mutation.finished.success, router.back);

sample({
	clock: authorizedCashdeskRoute.closed,
	target: [form.reset, getCashdeskQuery.reset, getCashiersQuery.reset, editCashdeskMutation.reset],
});

export const cashdeskPageModel = {
	getCashdeskQuery,
	editCashdeskMutation,
	withdrawCashdeskModel,
	depositCashdeskModel,
	blockCashdeskModel,
	unblockCashdeskModel,
	deleteCashdeskModel,
	form,
	$cashdesk,
	$cashiers,
	$pending,
};
